import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  GridItem,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Button,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useColorModeValue,
  Icon,
  Flex,
  Spacer,
  useToast,
  Progress,
  Avatar
} from '@chakra-ui/react';
import {
  FiBook,
  FiUsers,
  FiUpload,
  FiMessageCircle,
  FiEdit,
  FiPlus,
  FiEye,
  FiBarChart3,
  FiFileText
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../services/axiosInstance';
import Layout from '../shared/Layout';

const InstructorDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useToast();
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/instructor/dashboard');
      setDashboardData(response.data.data);
    } catch (error) {
      toast({
        title: 'خطأ في تحميل البيانات',
        description: 'حدث خطأ أثناء تحميل بيانات لوحة التحكم',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon, color = 'blue' }) => (
    <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
      <CardBody>
        <Flex>
          <Box>
            <Stat>
              <StatLabel fontSize="sm" color="gray.500">{title}</StatLabel>
              <StatNumber fontSize="2xl" fontWeight="bold">{value}</StatNumber>
              {subtitle && (
                <StatHelpText fontSize="sm">{subtitle}</StatHelpText>
              )}
            </Stat>
          </Box>
          <Spacer />
          <Box>
            <Icon as={icon} boxSize={8} color={`${color}.500`} />
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );

  const QuickActionCard = ({ title, description, icon, onClick, color = 'blue' }) => (
    <Card 
      bg={cardBg} 
      borderColor={borderColor} 
      borderWidth="1px"
      cursor="pointer"
      onClick={onClick}
      _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
      transition="all 0.2s"
    >
      <CardBody>
        <VStack spacing={3}>
          <Icon as={icon} boxSize={8} color={`${color}.500`} />
          <Text fontWeight="bold" textAlign="center">{title}</Text>
          <Text fontSize="sm" color="gray.500" textAlign="center">{description}</Text>
        </VStack>
      </CardBody>
    </Card>
  );

  if (loading) {
    return (
      <Layout>
        <Container maxW="7xl" py={8}>
          <Text>جاري التحميل...</Text>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxW="7xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box>
            <Heading size="lg" mb={2}>لوحة تحكم المدرس</Heading>
            <Text color="gray.500">إدارة دوراتك وطلابك</Text>
          </Box>

          {/* Statistics Cards */}
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={6}>
            <StatCard
              title="دوراتي"
              value={dashboardData?.courses?.total || 0}
              subtitle="الدورات التي أدرسها"
              icon={FiBook}
              color="blue"
            />
            <StatCard
              title="إجمالي الطلاب"
              value={dashboardData?.courses?.totalStudents || 0}
              subtitle={`متوسط ${dashboardData?.courses?.averageStudentsPerCourse || 0} طالب/دورة`}
              icon={FiUsers}
              color="green"
            />
            <StatCard
              title="الملفات المرفوعة"
              value={dashboardData?.uploads?.total || 0}
              subtitle={`${dashboardData?.uploads?.pending || 0} في انتظار المراجعة`}
              icon={FiUpload}
              color="orange"
            />
            <StatCard
              title="التعليقات المعلقة"
              value={dashboardData?.comments?.pending || 0}
              subtitle="تحتاج للرد"
              icon={FiMessageCircle}
              color="purple"
            />
          </Grid>

          {/* Quick Actions */}
          <Box>
            <Heading size="md" mb={4}>الإجراءات السريعة</Heading>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={4}>
              <QuickActionCard
                title="إدارة الدورات"
                description="تحرير محتوى دوراتك"
                icon={FiEdit}
                onClick={() => navigate('/instructor/courses')}
                color="blue"
              />
              <QuickActionCard
                title="إنشاء اختبار"
                description="إضافة اختبار جديد"
                icon={FiPlus}
                onClick={() => navigate('/instructor/qcm/create')}
                color="green"
              />
              <QuickActionCard
                title="مراجعة الملفات"
                description="مراجعة ملفات الطلاب"
                icon={FiFileText}
                onClick={() => navigate('/instructor/uploads')}
                color="orange"
              />
              <QuickActionCard
                title="الرد على التعليقات"
                description="الرد على تعليقات الطلاب"
                icon={FiMessageCircle}
                onClick={() => navigate('/instructor/comments')}
                color="purple"
              />
            </Grid>
          </Box>

          {/* Course Statistics */}
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardHeader>
              <Flex>
                <Heading size="md">إحصائيات الدورات</Heading>
                <Spacer />
                <Button 
                  size="sm" 
                  leftIcon={<FiEye />}
                  onClick={() => navigate('/instructor/courses')}
                >
                  عرض الكل
                </Button>
              </Flex>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                {dashboardData?.courseStats?.map((course, index) => (
                  <Box key={index} p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                    <Flex align="center" mb={3}>
                      <Box>
                        <Text fontWeight="bold">{course.courseName}</Text>
                        <Text fontSize="sm" color="gray.500">
                          {course.enrollmentCount} طالب مسجل
                        </Text>
                      </Box>
                      <Spacer />
                      <Badge colorScheme={course.completionRate > 70 ? 'green' : course.completionRate > 40 ? 'yellow' : 'red'}>
                        {course.completionRate}% معدل الإكمال
                      </Badge>
                    </Flex>
                    <Progress 
                      value={course.averageProgress} 
                      colorScheme={course.averageProgress > 70 ? 'green' : course.averageProgress > 40 ? 'yellow' : 'red'}
                      size="sm"
                      borderRadius="md"
                    />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      متوسط التقدم: {course.averageProgress}%
                    </Text>
                  </Box>
                )) || <Text color="gray.500">لا توجد دورات</Text>}
              </VStack>
            </CardBody>
          </Card>

          {/* Recent Activity */}
          <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6}>
            {/* Recent Student Activity */}
            <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
              <CardHeader>
                <Heading size="md">النشاط الحديث للطلاب</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={3} align="stretch">
                  {dashboardData?.recentActivity?.map((activity, index) => (
                    <Flex key={index} align="center">
                      <Avatar size="sm" name={activity.user?.username} mr={3} />
                      <Box>
                        <Text fontWeight="bold" fontSize="sm">{activity.user?.username}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {activity.course?.name} - آخر نشاط: {new Date(activity.lastAccessedAt).toLocaleDateString('ar-SA')}
                        </Text>
                      </Box>
                      <Spacer />
                      <Badge colorScheme={activity.overallProgress > 70 ? 'green' : activity.overallProgress > 40 ? 'yellow' : 'red'}>
                        {activity.overallProgress}%
                      </Badge>
                    </Flex>
                  )) || <Text color="gray.500">لا يوجد نشاط حديث</Text>}
                </VStack>
              </CardBody>
            </Card>

            {/* Quick Stats */}
            <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
              <CardHeader>
                <Heading size="md">إحصائيات سريعة</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4}>
                  <Flex w="full" justify="space-between">
                    <Text>الطلاب النشطون هذا الأسبوع</Text>
                    <Text fontWeight="bold" color="green.500">
                      {dashboardData?.weeklyActiveStudents || 0}
                    </Text>
                  </Flex>
                  <Flex w="full" justify="space-between">
                    <Text>متوسط وقت المشاهدة</Text>
                    <Text fontWeight="bold" color="blue.500">
                      {dashboardData?.averageWatchTime || 0} دقيقة
                    </Text>
                  </Flex>
                  <Flex w="full" justify="space-between">
                    <Text>الاختبارات المكتملة</Text>
                    <Text fontWeight="bold" color="purple.500">
                      {dashboardData?.completedQCMs || 0}
                    </Text>
                  </Flex>
                  <Flex w="full" justify="space-between">
                    <Text>معدل نجاح الاختبارات</Text>
                    <Text fontWeight="bold" color="orange.500">
                      {dashboardData?.qcmSuccessRate || 0}%
                    </Text>
                  </Flex>
                </VStack>
              </CardBody>
            </Card>
          </Grid>
        </VStack>
      </Container>
    </Layout>
  );
};

export default InstructorDashboard;

